import { useEffect, useMemo, useState } from "react";
import { IncludeOrExclude, RuleRunCriteriaField } from "@rebuild/shared";

interface FilterableSelectProps {
	options: string[];
	value?: string;
	onSelect: (value: string | undefined) => void;
	placeholder?: string;
	disabled?: boolean;
}

export const FilterableSelect: React.FC<FilterableSelectProps> = ({ options, value, onSelect, disabled, placeholder = "Select..." }) => {
	const [searchTerm, setSearchTerm] = useState<string>();
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setSearchTerm(value ?? "");
	}, [value]);

	const filteredOptions = useMemo(
		() => (!!searchTerm ? options.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase())) : options),
		[options, searchTerm],
	);

	const handleSelect = (option: string) => {
		onSelect(option);
		setIsOpen(false);
	};

	return (
		<div className="relative">
			<input
				type="text"
				disabled={disabled}
				value={searchTerm || ""}
				onChange={(e) => setSearchTerm(e.target.value)}
				onFocus={() => {
					setSearchTerm("");
					setIsOpen(true);
				}}
				onBlur={() => {
					setSearchTerm(value ?? "");
					setIsOpen(false);
				}}
				className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
				placeholder={placeholder}
			/>
			{(value || searchTerm) && (
				<button
					disabled={disabled}
					onClick={() => onSelect(undefined)}
					className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl"
				>
					×
				</button>
			)}
			{isOpen && filteredOptions.length > 0 && (
				<div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
					{(filteredOptions || []).map((option, index) => (
						<button
							key={index}
							onMouseDown={() => handleSelect(option)}
							className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
						>
							{option}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

interface MultiSelectProps {
	options: string[];
	values?: string[];
	onUpdate: (values?: string[]) => void;
	placeholder?: string;
	disabled?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({ options, values, onUpdate, placeholder, disabled }) => {
	const onSelect = (option: string) => {
		onUpdate([...(values ?? []), option]);
	};

	const onRemove = (index: number) => {
		if (values?.length === 1) {
			onUpdate(undefined);
		} else {
			onUpdate((values ?? []).filter((_, i) => i !== index));
		}
	};

	return (
		<div className="flex flex-col">
			<FilterableSelect
				options={options}
				onSelect={(option) => option && onSelect(option)}
				value={undefined}
				disabled={disabled}
				placeholder={placeholder}
			/>
			<div className="flex flex-wrap gap-1 mt-2">
				{(values ?? []).map((value, index) => (
					<div className="inline-flex items-center gap-1 px-3 py-2 bg-blue-100 border border-blue-200 rounded-md text-sm font-medium text-blue-800 h-8">
						<span className="whitespace-nowrap">{value}</span>
						<button
							onClick={() => onRemove(index)}
							className="hover:bg-blue-200 rounded-full w-4 h-4 flex items-center justify-center text-blue-600 text-xs"
							aria-label="Remove tag"
							disabled={disabled}
						>
							×
						</button>
					</div>
				))}
			</div>
		</div>
	);
};

interface FilterableSelectGenericProps<T> {
	options: T[];
	value?: T;
	onSelect: (value: T | undefined) => void;
	getDisplayValue: (obj: T) => string;
	getKey?: (obj: T) => string;
	placeholder?: string;
	disabled?: boolean;
}

export const FilterableSelectGeneric = <T extends any>({
	options,
	value,
	onSelect,
	getDisplayValue,
	getKey = getDisplayValue,
	disabled,
	placeholder = "Select...",
}: FilterableSelectGenericProps<T>) => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setSearchTerm(value ? getDisplayValue(value) : "");
	}, [value, getDisplayValue]);

	const filteredOptions = useMemo(
		() => (searchTerm ? options.filter((option) => getDisplayValue(option).toLowerCase().includes(searchTerm.toLowerCase())) : options),
		[options, searchTerm, getDisplayValue],
	);

	const handleSelect = (option: T) => {
		onSelect(option);
		setIsOpen(false);
	};

	return (
		<div className="relative">
			<input
				type="text"
				disabled={disabled}
				value={searchTerm || ""}
				onChange={(e) => setSearchTerm(e.target.value)}
				onFocus={() => {
					setSearchTerm("");
					setIsOpen(true);
				}}
				onBlur={() => {
					setSearchTerm(value ? getDisplayValue(value) : "");
					setIsOpen(false);
				}}
				className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
				placeholder={placeholder}
			/>
			{(value || searchTerm) && (
				<button
					disabled={disabled}
					onClick={() => onSelect(undefined)}
					className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl"
				>
					×
				</button>
			)}
			{isOpen && filteredOptions.length > 0 && (
				<div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
					{filteredOptions.map((option) => (
						<button
							key={getKey(option)}
							onMouseDown={() => handleSelect(option)}
							className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
						>
							{getDisplayValue(option)}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

interface MultiSelectGenericProps<T> {
	options: T[];
	values?: T[];
	onUpdate: (values?: T[]) => void;
	getDisplayValue: (obj: T) => string;
	getKey?: (obj: T) => string;
	placeholder?: string;
	disabled?: boolean;
}

export const MultiSelectGeneric = <T extends any>({
	options,
	values,
	onUpdate,
	getDisplayValue,
	getKey = getDisplayValue,
	placeholder,
	disabled,
}: MultiSelectGenericProps<T>) => {
	const onSelect = (option: T) => {
		onUpdate([...(values ?? []), option]);
	};

	const onRemove = (index: number) => {
		if (values?.length === 1) {
			onUpdate(undefined);
		} else {
			onUpdate((values ?? []).filter((_, i) => i !== index));
		}
	};

	return (
		<div className="flex flex-col">
			<FilterableSelectGeneric
				options={options}
				onSelect={(option) => option && onSelect(option)}
				value={undefined}
				disabled={disabled}
				placeholder={placeholder}
				getDisplayValue={getDisplayValue}
				getKey={getKey}
			/>
			<div className="flex flex-wrap gap-1 mt-2">
				{(values ?? []).map((value, index) => (
					<div
						key={getKey(value)}
						className="inline-flex items-center gap-1 px-3 py-2 bg-blue-100 border border-blue-200 rounded-md text-sm font-medium text-blue-800 h-8"
					>
						<span className="whitespace-nowrap">{getDisplayValue(value)}</span>
						<button
							onClick={() => onRemove(index)}
							className="hover:bg-blue-200 rounded-full w-4 h-4 flex items-center justify-center text-blue-600 text-xs"
							aria-label="Remove tag"
							disabled={disabled}
						>
							×
						</button>
					</div>
				))}
			</div>
		</div>
	);
};

interface IncludeExcludeToggleProps {
	value: "INCLUDE" | "EXCLUDE";
	onChange: (value: "INCLUDE" | "EXCLUDE") => void;
}

const IncludeExcludeToggle: React.FC<IncludeExcludeToggleProps> = ({ value, onChange }) => {
	const isInclude = value === "INCLUDE";

	return (
		<div className="relative inline-flex bg-gray-100 rounded p-0.5 cursor-pointer select-none py-2">
			<div
				className={`absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] bg-gray-500 rounded transition-all duration-200 ease-in-out ${
					isInclude ? "left-0.5" : "left-[calc(50%+1px)]"
				}`}
			/>
			<button
				onClick={() => onChange("INCLUDE")}
				className={`relative px-2 py-0.5 text-xs font-medium rounded transition-colors duration-200 ${isInclude ? "text-white" : "text-gray-500"}`}
			>
				INCLUDE
			</button>
			<button
				onClick={() => onChange("EXCLUDE")}
				className={`relative px-2 py-0.5 text-xs font-medium rounded transition-colors duration-200 ${!isInclude ? "text-white" : "text-gray-500"}`}
			>
				EXCLUDE
			</button>
		</div>
	);
};

interface GenericRuleRunCriteriaInputProps<T> {
	ruleRunCriteriaList: RuleRunCriteriaField | undefined;
	options: T[] | undefined;
	onUpdate: (values?: RuleRunCriteriaField) => void;
	getDisplayValue: (obj: T) => string;
	getObjId: (obj: T) => string;
}

export const GenericRuleRunCriteriaInput = <T extends any>({
	ruleRunCriteriaList,
	options,
	onUpdate,
	getObjId,
	getDisplayValue,
}: GenericRuleRunCriteriaInputProps<T>) => {
	const includeOrExclude = ruleRunCriteriaList?.includeOrExclude ?? "INCLUDE";
	const ids = ruleRunCriteriaList?.ids ?? [];

	const optionsListIsEmpty = !options?.length;

	const optionsById = !optionsListIsEmpty
		? options!.reduce(
				(acc, option) => {
					acc[getObjId(option)] = option;
					return acc;
				},
				{} as Record<string, T>,
			)
		: {};

	// If we have a value that's passed in that we can match to one of the options, we drop that value. This typically
	// happens when the user selects a carrier/tpa/org that has been archived and is therefore no longer included in
	// the list of options.
	const values = !optionsListIsEmpty ? ids.map((id) => optionsById[id]).filter((value) => value !== undefined) : [];

	const onGenericListUpdate = (newValues: T[] | undefined) => {
		if (newValues === undefined || newValues.length === 0) {
			onUpdate?.(undefined);
		} else {
			const newIds = newValues.map(getObjId);
			const newGenericList: RuleRunCriteriaField = {
				includeOrExclude,
				ids: newIds,
			};
			onUpdate?.(newGenericList);
		}
	};

	const onIncludeExcludeChange = (value: IncludeOrExclude) => {
		const newCriteriaList: RuleRunCriteriaField = {
			includeOrExclude: value,
			ids: ids,
		};
		onUpdate?.(newCriteriaList);
	};

	return (
		<div className="flex flex-row items-start gap-2">
			<IncludeExcludeToggle value={includeOrExclude} onChange={onIncludeExcludeChange} />
			<div className="flex-1">
				<MultiSelectGeneric
					key={`${JSON.stringify({ includeOrExclude, values })}`}
					values={values}
					options={options ?? []}
					getDisplayValue={getDisplayValue}
					onUpdate={onGenericListUpdate}
					disabled={optionsListIsEmpty}
				/>
			</div>
		</div>
	);
};
